import React, { useMemo, useRef, useCallback, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import {
    DataGrid,
    Column,
    Scrolling,
    LoadPanel,
    Editing,
    ColumnChooser,
    ColumnChooserSearch,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import notify from "devextreme/ui/notify";
import routes from "../../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../../common/GridLayout";
import GridLayoutManager from "../../../../../gridLayoutCustomization/GridLayoutManager";

const omStageDisplay = {
    L: "Canceled",
    Q: "Quote",
    P: "Plan",
    F: "Future Order",
    N: "Pending",
    R: "Ready To Receive/Pick/Staged",
    A: "Assigned",
    S: "Picked/Staged",
    C: "Completed",
};

const omStatusDisplay = {
    O: "Open",
    H: "Hold",
    L: "Ready To Commit Later",
    N: "Ready To Commit",
    Z: "Committed",
    A: "Active",
};

const emptyGuid = "00000000-0000-0000-0000-000000000000";

function OpenTransactionsForItemLotPopup(props) {
    const { showPopup = {}, setShowPopup, oidc } = props;
    const visible = showPopup.visible === true;
    const itemId = showPopup.itemId;
    const lotId = showPopup.lotId;
    const accessToken = oidc?.user?.access_token;

    const dataGridRef = useRef(null);
    const gridKey = "CD310C4E-651B-4EE0-9CC6-3388BCC76A64";
    const [currentLayout, setCurrentLayout] = useState("Default");
    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout);
    }, [currentLayout, accessToken]);
    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout);
    }, [gridKey, currentLayout, accessToken]);

    const dataSource = useMemo(() => {
        if (!itemId || !accessToken) return null;

        const lotParam = lotId && lotId !== emptyGuid ? lotId : undefined;
        const detailRowParam =
            showPopup.detailRowId && showPopup.detailRowId !== emptyGuid
                ? showPopup.detailRowId
                : undefined;

        return createStore({
            key: "Rowid",
            loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetTransactionDetailsForItemLot}`,
            updateUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.UpdateTransactionDetailsForItemLot}`,
            loadParams: {
                itemId,
                ...(lotParam ? { lotId: lotParam } : {}),
                ...(detailRowParam ? { rowid: detailRowParam } : {}),
            },
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                    access_token: accessToken,
                };
            },
            onModified() {
                notify({ message: "Quantity saved.", width: 260 }, "success", 2500);
                const grid = dataGridRef.current?.instance();
                if (grid) {
                    grid.cancelEditData();
                    grid.getDataSource().reload();
                }
            },
        });
    }, [itemId, lotId, showPopup.detailRowId, accessToken]);

    const handleHiding = useCallback(() => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const onRowUpdating = useCallback((e) => {
        e.newData.Originalquantity = e.oldData.Quantity;
    }, []);

    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={handleHiding}
            dragEnabled={false}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title={showPopup.title ?? "Open Transactions for Item/Lot"}
            container="#root"
            width="90vw"
            height="90vh"
        >
            {!dataSource ? (
                <div style={{ padding: 16 }}>
                    Missing item or session. Select an inventory line with an item and try again.
                </div>
            ) : (
                <DataGrid
                    key={gridKey}
                    ref={dataGridRef}
                    dataSource={dataSource}
                    columnAutoWidth={true}
                    allowColumnResizing={true}
                    allowColumnReordering={true}
                    hoverStateEnabled={true}
                    height="calc(100vh - 175px)"
                    showColumnLines={true}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    paging={false}
                    onRowUpdating={onRowUpdating}
                >
                    <Scrolling mode="standard" />
                    <LoadPanel enabled={true} />
                    <Editing mode="cell" allowUpdating={true} useIcons={true} refreshMode="reshape" />
                    <ColumnChooser
                        enabled={true}
                        title="Choose Column"
                        width={290}
                        height={290}
                        sortOrder="asc"
                    >
                        <ColumnChooserSearch
                            enabled={true}
                            editorOptions={{ placeholder: "Search column" }}
                        />
                    </ColumnChooser>
                    <Column dataField="Lastupdatedatetime" visible={false} allowEditing={false} />
                    <Column dataField="RLocation" visible={false} allowEditing={false} />
                    <Column dataField="locationId" visible={false} allowEditing={false} />
                    <Column dataField="IdentityID" caption="Customer" allowEditing={false} />
                    <Column dataField="Reference_1_Location" caption="Transaction#" allowEditing={false} />
                    <Column
                        dataField="Stage"
                        caption="Stage"
                        allowEditing={false}
                        calculateDisplayValue={(row) => omStageDisplay[row.Stage] || row.Stage || ""}
                    />
                    <Column
                        dataField="Status"
                        caption="Status"
                        allowEditing={false}
                        calculateDisplayValue={(row) => omStatusDisplay[row.Status] || row.Status || ""}
                    />
                    <Column dataField="Source" caption="Invoice Printed" allowEditing={false} />
                    <Column
                        dataField="Quantity"
                        caption="Quantity"
                        dataType="number"
                        allowEditing={true}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="Quantity_1_Location"
                        caption="Back Ordered"
                        dataType="number"
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="Grossweight"
                        caption="Amount"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column dataField="AvailableflagDiscription" caption="Available" visible={false} allowEditing={false} />
                    <Column dataField="Creationdatetime" caption="Created" visible={false} allowEditing={false} dataType="datetime" />
                    <Column dataField="CreationDateTime" caption="Detail Created" visible={false} allowEditing={false} dataType="datetime" />
                    <Column dataField="Creationuserid" caption="Created By" visible={false} allowEditing={false} />
                    <Column dataField="Lastupdateuserid" caption="Last Updated By" visible={false} allowEditing={false} />
                    <Column dataField="locationcode" caption="Location" visible={false} allowEditing={false} />
                    <Column dataField="Module" caption="Module" visible={false} allowEditing={false} />
                    <Column
                        dataField="OnHand"
                        caption="Ordered"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="Netweight"
                        caption="Original Amount"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="ItemAvailable"
                        caption="Original Ordered"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="Originalquantity"
                        caption="Original Quantity"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column dataField="Availablereasoncode" caption="Reason" visible={false} allowEditing={false} />
                    <Column dataField="Date_1_Location" caption="Ship Date" visible={false} allowEditing={false} dataType="datetime" />
                    <Column
                        dataField="OutShipped"
                        caption="Shipped"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column dataField="Transactiondate" caption="Transaction Date" visible={false} allowEditing={false} dataType="datetime" />
                    <Column
                        dataField="ForSale"
                        caption="Unit Price"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "currency", precision: 4, currency: "USD" }}
                    />
                    <Column dataField="Recordtype" visible={false} allowEditing={false} />
                    <Column dataField="Sourcetablename" visible={false} allowEditing={false} />
                    <Column dataField="Quantityinventory" visible={false} allowEditing={false} />
                    <Column dataField="Quantityinventoryuom" visible={false} allowEditing={false} />
                    <Column dataField="Code_1_Location" visible={false} allowEditing={false} />
                    <Column dataField="InOrdered" visible={false} allowEditing={false} />
                    <Column dataField="InReceiving" visible={false} allowEditing={false} />
                    <Column dataField="InBackorder" visible={false} allowEditing={false} />
                    <Column dataField="OutOrdered" visible={false} allowEditing={false} />
                    <Column dataField="OutShipped" visible={false} allowEditing={false} />
                    <Column dataField="OutBackorder" visible={false} allowEditing={false} />
                    <Column dataField="InUnavailable" visible={false} allowEditing={false} />
                    <Column dataField="OutUnavailable" visible={false} allowEditing={false} />
                    <Column dataField="InFuture" visible={false} allowEditing={false} />
                    <Column dataField="OutFuture" visible={false} allowEditing={false} />
                    <GridToolbar>
                        <GridToolbarItem location="after">
                            <GridLayoutManager
                                gridKey={gridKey}
                                dataGrid={dataGridRef}
                                setCurrentLayout={setCurrentLayout}
                            />
                        </GridToolbarItem>
                        <GridToolbarItem name="exportButton" />
                        <GridToolbarItem name="columnChooserButton" />
                    </GridToolbar>
                </DataGrid>
            )}
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(OpenTransactionsForItemLotPopup);
